import { Component, Input, Signal, SimpleChanges } from '@angular/core'
import {
    ConfirmedSessionsResponse,
    Container,
    SESSION_ACTIVITY_TYPE_ANYTHING,
    SESSION_ACTIVITY_TYPE_DESK,
    SESSION_ACTIVITY_TYPE_MOVING,
    SessionActivityType,
    SessionPreferenceFavorites,
    User,
} from '@app/core/_models'
import {
    BookSessionsService,
    CancelSessionService,
    ConfirmedSessionsService,
    UtilsService,
} from '@app/core/_services'
import { UserService } from '@app/core/_services/user.service'
import { combineLatestWith, Subject, take, takeUntil } from 'rxjs'
import { faLampDesk, faPersonWalking, faShuffle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { ToggleSwitchModule } from 'primeng/toggleswitch'
import { FormsModule } from '@angular/forms'
import {
    ContentStackItemOptions,
    ContentStackService,
} from '@app/core/_services/content-stack.service'
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min'
import { UserDateFormattingService } from '@app/core/_services/user-date-formatting.service'
import { IconFieldModule } from 'primeng/iconfield'
import { InputIconModule } from 'primeng/inputicon'
import { ButtonModule } from 'primeng/button'
import { InputTextModule } from 'primeng/inputtext'
import { EditSessionService } from '@app/core/_services/edit-session.service'
import { AutoFocusModule } from 'primeng/autofocus'
import { FmAvatarComponent } from '@app/shared/components/fm-avatar/fm-avatar.component'
import {
    AnalyticsService,
    BookingSource,
    SessionTileSource,
} from '@app/core/_services/analytics.service'
import { SharedModule } from '@app/shared/shared.module'
import { faLock } from '@fortawesome/pro-solid-svg-icons'
import { SessionStateService } from '@app/core/_services/session-state.service'
import { SelectModule } from 'primeng/select'
import { SelectButtonChangeEvent, SelectButtonModule } from 'primeng/selectbutton'
import { CommonModule } from '@angular/common'
import { ErrorHandlerService } from '@app/core/_services/error-handler.service'
import { DialogService } from '@app/core/_services/dialog.service'
import { MODAL_VERIFY_EMAIL_BOOKING } from '@app/shared/components/email-verify/email-verify.component'
import { RewardsReferralService } from '@app/core/_services/rewards-referral.service'
import { WelcomeChecklistService } from '@app/core/_services/welcome-checklist.service'
import { TooltipModule } from 'primeng/tooltip'
import { ScrollPanelModule } from 'primeng/scrollpanel'

// Define options arrays for Activity, Favarites preferences and recurring sessions
// TODO: Consider a central location for these as this drop downs exist in multiple
// componenents
interface ActivityOption {
    icon: any
    label: string
    value: SessionActivityType
}

const ACTIVITY_OPTIONS: ActivityOption[] = [
    { icon: faLampDesk, label: 'Desk', value: SESSION_ACTIVITY_TYPE_DESK },
    { icon: faPersonWalking, label: 'Moving', value: SESSION_ACTIVITY_TYPE_MOVING },
    { icon: faShuffle, label: 'Anything', value: SESSION_ACTIVITY_TYPE_ANYTHING },
]

interface FavoritesPreferenceOption {
    value: SessionPreferenceFavorites
    name: string
    icon: string
}

const FAVORITES_PREFERENCE_OPTIONS: FavoritesPreferenceOption[] = [
    { value: 'noPreference', name: 'No preference', icon: 'pi pi-globe' },
    { value: 'preferred', name: 'Prefer favorites', icon: 'pi pi-star' },
    { value: 'onlyFavorites', name: 'Only favorites', icon: 'pi pi-star-fill' },
]

interface RepeatTypeOption {
    icon: any
    name: string
    value: number
}
const REPEAT_TYPE_OPTIONS: RepeatTypeOption[] = [
    { value: 3, name: ' Repeat weekly', icon: 'pi pi-refresh' },
    { value: 2, name: ' Repeat daily', icon: 'pi pi-sun' },
]

interface RepeatFrequencyOption {
    name: string
    value: number
}
const REPEAT_FREQUENCY_OPTIONS: RepeatFrequencyOption[] = [
    { value: 1, name: '1 Time' },
    { value: 2, name: '2 Times' },
    { value: 3, name: '3 Times' },
    { value: 4, name: '4 Times' },
    { value: 5, name: '5 Times' },
    { value: 6, name: '6 Times' },
    { value: 7, name: '7 Times' },
    { value: 8, name: '8 Times' },
    { value: 9, name: '9 Times' },
    { value: 10, name: '10 Times' },
]

const UPDATE_PREFERENCES_ERROR_TEXT = 'Preferences cannot be changed once session has started.'

@Component({
    selector: 'app-rp-session-info',
    imports: [
        ScrollPanelModule,
        TooltipModule,
        CommonModule,
        SelectModule,
        SelectButtonModule,
        FmAvatarComponent,
        SharedModule,
        AutoFocusModule,
        InputTextModule,
        ButtonModule,
        IconFieldModule,
        InputIconModule,
        FormsModule,
        FontAwesomeModule,
        ToggleSwitchModule,
    ],
    templateUrl: './rp-session-info.component.html',
    styleUrl: './rp-session-info.component.scss',
})
export class RpSessionInfoComponent {
    @Input() sessionTime: number

    public sessionInfo: ConfirmedSessionsResponse | null
    public currentUser: User
    public sessionDateAndDurationStr: string
    public sessionTimeStr: string

    // Activity
    public currentActivity: ActivityOption
    public selectedActivity: ActivityOption
    public activityOptions = ACTIVITY_OPTIONS

    // Quiet Mode
    public currentQuietMode: boolean
    public selectedQuietMode: boolean

    // Favorites prerence
    public currentFavoritesSelection: FavoritesPreferenceOption
    public selectedFavoritesSelection: FavoritesPreferenceOption
    public favoritesPreferenceOptions = FAVORITES_PREFERENCE_OPTIONS

    // Repeat type
    public repeatTypeOptions = REPEAT_TYPE_OPTIONS
    public selectedRepeatType = this.repeatTypeOptions[0]

    // Repeat frequency
    public repeatFrequencyOptions = REPEAT_FREQUENCY_OPTIONS
    public selectedRepeatFrequency = this.repeatFrequencyOptions[0]

    // Title
    public editTitle: boolean = false
    public updatingTitle: boolean = false
    public newTitle: string = ''

    // Partner Info
    public numSessionsString: string
    public timezoneAndTimeString: string
    public messageUrl: string = ''
    public faLock = faLock
    public lockedInPartner: boolean = false

    // Session settings
    public settingsOptionsExpanded: boolean = false
    public updateButtonDisabled: boolean = true
    public updatingPreferences: boolean = false
    public updateSessionAllowed: boolean = true

    // Repeat settings
    public repeatSessionExpanded: boolean = false
    public bookingRepeatSessions: boolean = false

    // Session join/cancel
    public joinSessionEnabled: boolean
    public cancelSessionEnabled: boolean
    public cancelInProgress: boolean = false

    // Misc
    private isWelcomeChecklistActive: Signal<boolean> =
        this.welcomeChecklistService.isWelcomeChecklistActive
    public confirmedSessionsArray: Container<ConfirmedSessionsResponse> =
        new Container<ConfirmedSessionsResponse>()

    constructor(
        private userService: UserService,
        private confirmedSessionsService: ConfirmedSessionsService,
        private contentStackService: ContentStackService,
        private dateFormat: UserDateFormattingService,
        private editSessionService: EditSessionService,
        private analyticsService: AnalyticsService,
        private utilsService: UtilsService,
        private sessionStateService: SessionStateService,
        private bookSessionService: BookSessionsService,
        private errorHandlerService: ErrorHandlerService,
        private dialogService: DialogService,
        private rewardsReferralService: RewardsReferralService,
        private welcomeChecklistService: WelcomeChecklistService,
        private cancelSessionService: CancelSessionService,
    ) {}
    private ngUnsubscribe: Subject<any> = new Subject<any>()

    public onCancelEditTitle(): void {
        this.newTitle = ''
        this.editTitle = false
    }

    public onUpdateSessionTitle(): void {
        this.updatingTitle = true

        this.editSessionService
            .editSessionTitle(this.sessionInfo.sessionTime, this.newTitle)
            .subscribe({
                next: (v) => {
                    this.updatingTitle = false
                    this.editTitle = false
                },
                error: (e) => {
                    this.updatingTitle = false
                    this.editTitle = false
                },
            })
    }

    onUserProfileSelected() {
        this.analyticsService.logClickedEvent('User', 'Timeslot Partners: List Item')

        const options: ContentStackItemOptions = {
            popIfCovered: true,
            preserveStack: false,
        }
        this.contentStackService.pushProfileToDynamicPanel(
            this.sessionInfo.partnerProfileUrl,
            options,
        )
    }

    private setInitialActivityType() {
        this.currentActivity = this.activityOptions.find(
            (item) => item.value === this.sessionInfo.activityType,
        )
        if (!this.selectedActivity) {
            this.selectedActivity = this.currentActivity
        }
    }

    private setInitialFavoritesPreference() {
        this.currentFavoritesSelection = this.favoritesPreferenceOptions.find(
            (item) => item.value === this.sessionInfo.preferences.favorites.value,
        )
        if (!this.selectedFavoritesSelection) {
            this.selectedFavoritesSelection = this.currentFavoritesSelection
        }
    }

    private setInitialQuietMode() {
        this.currentQuietMode = this.sessionInfo.preferences.quietMode.value

        if (this.selectedQuietMode === undefined) {
            this.selectedQuietMode = this.currentQuietMode
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['sessionTime']) {
            if (!changes['sessionTime'].isFirstChange()) {
                this.ngUnsubscribe.next(null)
                this.ngUnsubscribe.complete()
                this.initializeComponent()
            }
        }
    }

    ngOnInit(): void {
        this.initializeComponent()
    }

    private initializeComponent(): void {
        this.userService.currentUser
            .pipe(
                combineLatestWith(this.confirmedSessionsService.confirmedSessionsSubject),
                takeUntil(this.ngUnsubscribe),
            )
            .subscribe(([currentUser, confirmSessions]) => {
                this.currentUser = currentUser
                this.sessionInfo = confirmSessions.getBySessionTime(this.sessionTime)
                this.confirmedSessionsArray = confirmSessions

                if (!this.sessionInfo || !this.currentUser.userId) {
                    // Session no longer exists
                    this.contentStackService.popContent()
                } else {
                    this.sessionTimeStr = this.dateFormat.format(this.sessionTime, 'h:mma')
                    this.sessionDateAndDurationStr = `${this.sessionInfo.duration / 1000 / 60} min · ${moment(this.sessionTime).format('dddd, MMM Do')}`

                    this.setInitialActivityType()
                    this.setInitialQuietMode()
                    this.setInitialFavoritesPreference()
                    this.setPartnerInfo()
                    this.messageUrl = this.utilsService.buildMessageLinkUrl(
                        this.sessionInfo.meetingRoom,
                        this.currentUser.email,
                    )
                    this.lockedInPartner = this.sessionStateService.isLockedInState(
                        this.sessionInfo.state,
                    )
                    this.updateJoinSessionButtonStatus()
                    this.cancelSessionEnabled = this.utilsService.canCancel(this.sessionTime)
                }
            })
    }

    public updateJoinSessionButtonStatus(): void {
        this.joinSessionEnabled = this.sessionStateService.isSessionJoinable(
            this.sessionInfo.sessionTime,
            this.sessionInfo.duration,
            this.sessionInfo.state,
        )
    }

    public setUpdateSettingsButtonStatus(): void {
        if (
            this.utilsService.hasSessionTimePassed(
                this.sessionInfo.sessionTime,
                this.utilsService.getCurrentTimestamp(),
            )
        ) {
            this.updateButtonDisabled = true
            this.updateSessionAllowed = false
        }
    }

    private setPartnerInfo() {
        if (!this.sessionInfo.user_comp_sessions) {
            this.numSessionsString = 'New'
        } else if (this.sessionInfo.user_comp_sessions === 1) {
            this.numSessionsString = '1 session'
        } else {
            this.numSessionsString = `${this.sessionInfo.user_comp_sessions} sessions`
        }
        if (this.sessionInfo.partnerTimezone) {
            this.timezoneAndTimeString = `${this.sessionInfo.partnerTimezone} · ${this.dateFormat.formatShortHand(this.sessionInfo.sessionTime, this.sessionInfo.partnerTimezone)}`
        }
    }

    public onClickSelectedActivity(index: number): void {
        this.selectedActivity = this.activityOptions[index]
    }

    public onChangeQuietMode(event: any) {
        this.selectedQuietMode = event.checked
    }

    shouldUpdateButtonBeDisabled() {
        if (this.currentActivity != this.selectedActivity) {
            return false
        }
        if (this.selectedFavoritesSelection != this.currentFavoritesSelection) {
            return false
        }
        if (this.currentQuietMode != this.selectedQuietMode) {
            return false
        }
        return true
    }

    onFavoritesPreferenceChanged(event: SelectButtonChangeEvent) {
        this.selectedFavoritesSelection = this.favoritesPreferenceOptions.find(
            (element) => element.value == event.value,
        )
        this.updateButtonDisabled = this.shouldUpdateButtonBeDisabled()
    }

    onQuietModeChanged(event) {
        this.updateButtonDisabled = this.shouldUpdateButtonBeDisabled()
    }

    onActivityTypeChanged(event) {
        this.selectedActivity = this.activityOptions.find((element) => element.value == event.value)
        this.updateButtonDisabled = this.shouldUpdateButtonBeDisabled()
    }

    onRecurFrequencyChanged(event: SelectButtonChangeEvent): void {
        this.selectedRepeatFrequency = this.repeatFrequencyOptions.find(
            (element) => element.value == event.value,
        )
    }

    onRecurTypeChanged(event: SelectButtonChangeEvent): void {
        this.selectedRepeatType = this.repeatTypeOptions.find(
            (element) => element.value == event.value,
        )
    }

    bookRecurringSessions() {
        this.bookingRepeatSessions = true
        // Increment by one since first time slot is the existing
        // slot that is going to be repeated
        this.bookSessionService
            .bookRecurringSessions(
                this.sessionInfo.sessionTime,
                this.selectedRepeatType.value,
                this.selectedRepeatFrequency.value + 1,
                this.currentUser.timeZone,
                this.sessionInfo.title,
                this.confirmedSessionsArray,
                this.sessionInfo.duration,
                this.sessionInfo.preferences.favorites.value,
                this.sessionInfo.activityType,
                this.sessionInfo.preferences.quietMode.value,
                BookingSource.SessionInfoModal,
            )
            .pipe(take(1))
            .subscribe(
                (sessions) => {
                    this.bookingRepeatSessions = false
                    let parsedRes = this.bookSessionService.parseBookResponse(sessions)

                    if (parsedRes['numSuccess'] === 1) {
                    } else if (parsedRes['numSuccess'] > 1) {
                    }

                    if (parsedRes['numSuccess'] !== 0) {
                        this.dialogService.openConfirmTransaction(
                            parsedRes['numSuccess'] === 1
                                ? 'Session booked!'
                                : parsedRes['numSuccess'] + ' sessions booked!',
                        )
                    }

                    if (this.currentUser.emailVerified === false) {
                        this.dialogService
                            .openEmailVerificationDialog(MODAL_VERIFY_EMAIL_BOOKING)
                            .afterClosed()
                            .pipe(take(1))
                            .subscribe(() => {
                                this.processParsedResult(parsedRes)
                            })
                    } else {
                        this.processParsedResult(parsedRes)
                    }
                },
                (error) => {
                    this.bookingRepeatSessions = false
                    this.errorHandlerService.handleErrorResponse(error)
                },
            )
    }

    processParsedResult(parsedRes) {
        if (parsedRes['planLimitReached'] === true) {
            this.dialogService.openPlanLimitReachedDialog()
        } else if (
            !this.isWelcomeChecklistActive() &&
            !this.currentUser.properties.viewedSegmentationSurveyModal &&
            this.currentUser.compSessions >= 3
        ) {
            this.dialogService.openSegmentationSurveyModal()
        } else if (
            !this.isWelcomeChecklistActive() &&
            this.rewardsReferralService.shouldSeeReferralPrompt(this.currentUser)
        ) {
            this.dialogService.openReferralPromptModal()
        }
    }

    public updatePreferences() {
        /* Once preference is updated allow for a small delay to allow the worker process
		on the backend to break up the match, if necessary, and find a new match */
        this.updatingPreferences = true
        this.editSessionService
            .editSessionPreferences(
                this.sessionInfo.sessionTime,
                this.selectedFavoritesSelection.value,
                this.selectedActivity.value,
                this.selectedQuietMode,
            )
            .subscribe(
                () => {
                    setTimeout(() => {
                        this.confirmedSessionsService.getConfirmedSessions().subscribe(() => {
                            this.updatingPreferences = false
                            this.updateButtonDisabled = true
                        })
                    }, 3000)
                },
                (error) => {
                    this.selectedFavoritesSelection = this.favoritesPreferenceOptions.find(
                        (element) => element.value == this.sessionInfo.preferences.favorites.value,
                    )
                    this.selectedQuietMode = this.sessionInfo.preferences.quietMode.value
                    this.selectedActivity = this.activityOptions.find(
                        (element) => element.value == this.sessionInfo.activityType,
                    )
                    this.updatingPreferences = false
                    this.updateButtonDisabled = true

                    if (
                        this.utilsService.hasSessionTimePassed(
                            this.sessionInfo.sessionTime,
                            this.utilsService.getCurrentTimestamp(),
                        )
                    ) {
                        // this.updateSessionTooltip = UPDATE_PREFERENCES_ERROR_TEXT
                        // this.hasSessionStarted = true
                        // this.showUpdatePreferencesError()
                    }
                },
            )
    }

    onCancelSession() {
        if (!this.cancelInProgress) {
            this.cancelInProgress = true
            this.cancelSessionService
                .cancelSession(this.sessionInfo.externalId, SessionTileSource.SessionInfoModal)
                .subscribe(
                    (res) => {
                        this.contentStackService.popContent()
                    },
                    (error) => {
                        this.contentStackService.popContent()
                    },
                )
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(null)
        this.ngUnsubscribe.complete()
    }
}
